import {
  EnvironmentInjector,
  Injectable,
  computed,
  createEnvironmentInjector,
  inject,
  signal,
} from '@angular/core';
import {
  EMPTY,
  Observable,
  Subject,
  Subscription,
  catchError,
  expand,
  finalize,
  interval,
  map,
  reduce,
  switchMap,
  tap,
  throwError,
} from 'rxjs';
import { SUPPORT_API } from '../support-system.config';
import {
  SupportApiChat,
  SupportApiMe,
  SupportApiMessage,
  SupportAttachment,
  SupportErrorCode,
  SupportInboxFilters,
  SupportMessage,
  SupportUploadEvent,
} from '../support-system.models';
import {
  SupportApiService,
  toSupportError,
} from '../services/support-api.service';
import {
  fromApiAttachment,
  fromApiMessage,
  mergeMessages,
  newClientId,
} from '../services/support-chat.service';
import { SupportSocketService } from '../services/support-socket.service';

export type SupportInboxTab = 'new' | 'mine' | 'issues';
export type SupportInboxAccess = 'checking' | 'ok' | 'denied' | 'error';

/** Team roles that may open the inbox (the backend's INBOX_ROLES). */
const INBOX_ROLES = ['MANAGER', 'CUSTOMER_SUCCESS'];

export interface SupportInboxThread {
  /** Oldest first. */
  messages: SupportMessage[];
  loaded: boolean;
  /** Cursor for older messages; null when everything is loaded. */
  olderCursor: string | null;
}

interface TabPage {
  loading: boolean;
  loaded: boolean;
  nextCursor: string | null;
  error: SupportErrorCode | null;
}

const EMPTY_PAGE: TabPage = {
  loading: false,
  loaded: false,
  nextCursor: null,
  error: null,
};

/**
 * The team side of `/api/v1/support`: inbox lists, one open thread, take
 * over, reply and close.
 *
 * - Every chat summary we see (REST pages, inbox socket events, take-overs)
 *   goes into one pool; the tabs are filters over it, matching the backend's
 *   filters, so a live update moves a chat between tabs by itself.
 * - Only the open thread's room is joined, so only its messages are pushed.
 * - Provided per view, with its own socket: the client chat-box disconnects
 *   the root socket for team accounts.
 */
@Injectable({ providedIn: 'root' })
export class SupportInboxService {
  private api = inject(SupportApiService);
  /**
   * Its own socket, not the root one: the client chat-box disconnects that
   * one for team accounts.
   */
  private socket = createEnvironmentInjector(
    [SupportSocketService],
    inject(EnvironmentInjector),
  ).get(SupportSocketService);

  readonly me = signal<SupportApiMe | null>(null);
  readonly access = signal<SupportInboxAccess>('checking');
  /** Managers take over, reply and close; CS can only read. */
  readonly canAct = computed(() => this.me()?.role === 'MANAGER');
  readonly connection = this.socket.state;
  /** False once the backend says uploads aren't configured (503). */
  readonly attachmentsAvailable = signal(true);

  readonly tab = signal<SupportInboxTab>('new');
  /** "My chats" shows open or closed ones. */
  readonly mineStatus = signal<'open' | 'closed'>('open');

  private readonly pool = signal<Record<string, SupportApiChat>>({});
  private readonly pages = signal<Record<string, TabPage>>({});
  private readonly _threads = signal<Record<string, SupportInboxThread>>({});
  readonly threads = this._threads.asReadonly();
  readonly selectedId = signal<string | null>(null);
  readonly selected = computed(() => {
    const id = this.selectedId();
    return id ? this.pool()[id] || null : null;
  });
  readonly thread = computed(() => {
    const id = this.selectedId();
    return id ? this._threads()[id] || null : null;
  });

  private readonly lists = computed(() => {
    const me = this.me()?.id;
    const mineStatus = this.mineStatus();
    const all = Object.values(this.pool()).sort(byActivity);
    return {
      new: all.filter((c) => matchesTab('new', c, me, mineStatus)),
      mine: all.filter((c) => matchesTab('mine', c, me, mineStatus)),
      issues: all.filter((c) => matchesTab('issues', c, me, mineStatus)),
    };
  });
  readonly list = computed(() => this.lists()[this.tab()]);
  /**
   * Open chats waiting for the team: the client wrote last, and nobody took
   * them yet or I did. Shown on the sidenav's Support link.
   */
  readonly waitingCount = computed(() => {
    const me = this.me()?.id;
    return Object.values(this.pool()).filter(
      (c) =>
        c.status === 'open' &&
        c.lastMessageSenderType === 'client' &&
        (!c.assignedManagerId || c.assignedManagerId === me),
    ).length;
  });
  private readonly incomingSubject = new Subject<SupportApiChat>();
  /**
   * A client wrote in a chat that is unassigned or mine. Only messages after
   * the first load, each once.
   */
  readonly incoming$ = this.incomingSubject.asObservable();

  readonly counts = computed(() => {
    const lists = this.lists();
    return { new: lists.new.length, mine: lists.mine.length, issues: lists.issues.length };
  });
  readonly page = computed(
    () => this.pages()[this.pageKey(this.tab())] || null,
  );

  private subscriptions = new Subscription();
  private readonly outbox = new Map<
    string,
    { chatId: string; body: string; attachmentIds: string[] }
  >();
  /** The room this socket is in (rooms are lost on reconnect). */
  private joinedId: string | null = null;
  private readonly loadingThreads = new Set<string>();
  private readonly catchingUp = new Set<string>();
  private lastListSync = Date.now();
  private meRequest: Subscription | null = null;
  /** Latest client message time per chat, to spot new ones. */
  private readonly seen = new Map<string, string>();
  /** Set once the first lists are in; earlier messages never notify. */
  private primedAfter: string | null = null;

  /**
   * Checks access and starts the lists and the socket. Runs app-wide (the
   * sidenav starts it for the badge), so calling it again only refreshes.
   */
  init(): void {
    if (this.access() === 'ok') {
      (['new', 'mine', 'issues'] as const).forEach((tab) => this.loadTab(tab, false, true));
      return;
    }
    if (this.meRequest) return;
    this.access.set('checking');
    this.meRequest = this.api.me().subscribe({
      next: (me) => {
        this.meRequest = null;
        this.me.set(me);
        if (me.kind !== 'team' || !INBOX_ROLES.includes(me.role || '')) {
          this.access.set('denied');
          return;
        }
        this.access.set('ok');
        this.connect();
        // All three, so every tab shows its count from the start. Messages
        // count as new (for notifications) only after these are in.
        let left = 3;
        const loaded = () => {
          if (--left === 0) this.prime();
        };
        (['new', 'mine', 'issues'] as const).forEach((tab) =>
          this.loadTab(tab, false, false, loaded),
        );
      },
      error: (error) => {
        this.meRequest = null;
        const code = toSupportError(error).code;
        this.access.set(
          code === 'FORBIDDEN' || code === 'UNAUTHORIZED' ? 'denied' : 'error',
        );
      },
    });
  }

  /** Stops everything and forgets the user (logout). */
  destroy(): void {
    this.meRequest?.unsubscribe();
    this.meRequest = null;
    this.subscriptions.unsubscribe();
    this.subscriptions = new Subscription();
    this.socket.disconnect();
    this.joinedId = null;
    this.me.set(null);
    this.access.set('checking');
    this.pool.set({});
    this.pages.set({});
    this._threads.set({});
    this.selectedId.set(null);
    this.outbox.clear();
    this.seen.clear();
    this.primedAfter = null;
  }

  /* ------------------------------- Lists ------------------------------- */

  setTab(tab: SupportInboxTab): void {
    this.tab.set(tab);
    if (!this.pages()[this.pageKey(tab)]?.loaded) this.loadTab(tab);
  }

  setMineStatus(status: 'open' | 'closed'): void {
    this.mineStatus.set(status);
    if (!this.pages()[this.pageKey('mine')]?.loaded) this.loadTab('mine');
  }

  /**
   * First page of a tab, or the next one with `more`. `quiet` (background
   * re-checks) shows no loading state and keeps errors to itself.
   */
  loadTab(
    tab: SupportInboxTab,
    more = false,
    quiet = false,
    done?: () => void,
  ): void {
    const key = this.pageKey(tab);
    const current = this.pages()[key];
    if (current?.loading || (more && !current?.nextCursor)) {
      done?.();
      return;
    }
    if (!quiet) this.setPage(key, { loading: true, error: null });
    this.api
      .inbox({ ...this.filtersFor(tab), cursor: more ? current?.nextCursor : null })
      .subscribe({
        next: (page) => {
          this.upsert(page.items);
          if (!more && !page.nextCursor) this.recheckMissing(tab, page.items);
          this.setPage(key, {
            loading: false,
            loaded: true,
            nextCursor: more || !current?.nextCursor ? page.nextCursor : current.nextCursor,
            error: null,
          });
          done?.();
        },
        error: (error) => {
          if (!quiet) {
            this.setPage(key, { loading: false, error: toSupportError(error).code });
          }
          done?.();
        },
      });
  }

  /* ------------------------------- Thread ------------------------------ */

  select(id: string | null): void {
    if (id === this.selectedId()) return;
    if (this.joinedId && this.joinedId !== id) {
      this.socket.leave(this.joinedId).subscribe({ error: () => {} });
      this.joinedId = null;
    }
    this.selectedId.set(id);
    if (!id) return;
    if (!this.pool()[id]) {
      // Opened from a link: the chat may be in no loaded page.
      this.api.chat(id).subscribe({
        next: (chat) => this.upsert([chat]),
        error: () => {},
      });
    }
    this.join(id);
    if (!this._threads()[id]?.loaded) this.loadMessages(id);
  }

  /** `quiet`: a background re-check of an empty thread. */
  loadMessages(id: string, quiet = false): void {
    if (this.loadingThreads.has(id)) return;
    if (quiet && !this._threads()[id]?.loaded) return;
    this.loadingThreads.add(id);
    this.api.messages(id).subscribe({
      next: (page) => {
        this.loadingThreads.delete(id);
        this.patchThread(id, (thread) => ({
          messages: mergeMessages(thread.messages, page.items.map(fromApiMessage)),
          loaded: true,
          olderCursor: page.nextCursor,
        }));
      },
      error: () => this.loadingThreads.delete(id),
    });
  }

  loadOlder(id: string): Observable<void> {
    const cursor = this._threads()[id]?.olderCursor;
    if (!cursor) return EMPTY;
    return this.api.messages(id, cursor).pipe(
      tap((page) =>
        this.patchThread(id, (thread) => ({
          ...thread,
          messages: mergeMessages(thread.messages, page.items.map(fromApiMessage)),
          olderCursor: page.nextCursor,
        })),
      ),
      map(() => undefined),
    );
  }

  /* ------------------------------ Actions ------------------------------ */

  /** Take over. On 409 the chat is reloaded (someone else has it). */
  claim(id: string): Observable<SupportApiChat> {
    return this.api.claim(id).pipe(
      tap((chat) => this.upsert([chat])),
      catchError((error) => this.reloadAfter(id, error)),
    );
  }

  close(id: string): Observable<SupportApiChat> {
    return this.api.close(id).pipe(
      tap((chat) => this.upsert([chat])),
      catchError((error) => this.reloadAfter(id, error)),
    );
  }

  send(chatId: string, body: string, attachments: SupportAttachment[] = []): void {
    const me = this.me();
    const clientId = newClientId();
    const message: SupportMessage = {
      id: clientId,
      clientId,
      conversationId: chatId,
      author: 'agent',
      authorName: me?.name,
      body,
      createdAt: new Date().toISOString(),
      status: 'sending',
      attachments: attachments.length ? attachments : undefined,
    };
    this.patchThread(chatId, (thread) => ({
      ...thread,
      messages: mergeMessages(thread.messages, [message]),
    }));
    this.outbox.set(clientId, {
      chatId,
      body,
      attachmentIds: attachments.map((a) => a.id),
    });
    this.deliver(clientId);
  }

  retry(message: SupportMessage): void {
    if (!this.outbox.has(message.clientId)) return;
    this.patchMessage(message.conversationId, message.clientId, {
      status: 'sending',
      error: undefined,
    });
    this.deliver(message.clientId);
  }

  uploadAttachment(chatId: string, file: File): Observable<SupportUploadEvent> {
    return this.api.reserveAttachment(chatId, file).pipe(
      switchMap(({ attachment, upload }) =>
        this.api.uploadToStorage(upload, file).pipe(
          map(
            (share): SupportUploadEvent =>
              share < 1
                ? { type: 'progress', loaded: share * file.size, total: file.size }
                : { type: 'done', attachment: fromApiAttachment(attachment) },
          ),
        ),
      ),
      catchError((error) => {
        const failure = toSupportError(error);
        if (failure.code === 'UNAVAILABLE') this.attachmentsAvailable.set(false);
        return throwError(() => failure);
      }),
    );
  }

  attachmentUrl(chatId: string, attachmentId: string): Observable<string> {
    return this.api.attachmentUrl(chatId, attachmentId).pipe(map((s) => s.url));
  }

  /* ------------------------------ Internals ----------------------------- */

  private connect(): void {
    this.subscriptions.add(
      this.socket.connected$.subscribe(({ reconnect }) => {
        this.socket.subscribeInbox().subscribe({ error: () => {} });
        this.joinedId = null;
        const selected = this.selectedId();
        if (selected) this.join(selected);
        if (reconnect) this.resync();
      }),
    );
    this.subscriptions.add(this.socket.chat$.subscribe((chat) => this.upsert([chat])));
    this.subscriptions.add(
      this.socket.message$.subscribe((message) => this.handleIncoming(message)),
    );
    this.subscriptions.add(interval(SUPPORT_API.threadSyncMs).subscribe(() => this.sync()));
    this.socket.connect();
  }

  private join(id: string): void {
    if (this.connection() !== 'connected' || this.joinedId === id) return;
    this.joinedId = id;
    this.socket.join(id).subscribe({
      next: (chat) => this.upsert([chat]),
      error: () => {
        if (this.joinedId === id) this.joinedId = null;
      },
    });
  }

  /**
   * After a reconnect: summaries may have changed while we were away, so the
   * lists start over, and the open thread fetches what it missed.
   */
  private resync(): void {
    const selected = this.selectedId();
    const keep = selected ? this.pool()[selected] : null;
    this.pool.set(keep ? { [keep._id]: keep } : {});
    this.pages.set({});
    (['new', 'mine', 'issues'] as const).forEach((tab) => this.loadTab(tab));
    if (selected) {
      this.api.chat(selected).subscribe({ next: (chat) => this.upsert([chat]), error: () => {} });
      this.catchUp(selected);
    }
  }

  private catchUp(id: string): void {
    const thread = this._threads()[id];
    const last = [...(thread?.messages || [])].reverse().find((m) => m.status === 'sent');
    if (!thread?.loaded || !last) {
      this.loadMessages(id, true);
      return;
    }
    if (this.catchingUp.has(id)) return;
    this.catchingUp.add(id);
    const fetch = (after: string) =>
      this.api.messagesAfter(id, after).pipe(
        map((page) => ({ ...page, last: page.items[page.items.length - 1]?._id })),
      );
    fetch(last.id)
      .pipe(
        expand((page) => (page.hasMore && page.last ? fetch(page.last) : EMPTY)),
        reduce((all: SupportApiMessage[], page) => [...all, ...page.items], []),
        finalize(() => this.catchingUp.delete(id)),
      )
      .subscribe({
        next: (items) =>
          this.patchThread(id, (t) => ({
            ...t,
            messages: mergeMessages(t.messages, items.map(fromApiMessage)),
          })),
        error: () => {},
      });
  }

  /**
   * Live events can be missed (lost between backend workers, a socket that
   * is down), so REST re-checks: the open thread every tick, the lists every
   * `listSyncMs`. Everything merges by id, so nothing is shown twice.
   */
  private sync(): void {
    if (document.hidden) return;
    if (Date.now() - this.lastListSync >= SUPPORT_API.listSyncMs) {
      this.lastListSync = Date.now();
      (['new', 'mine', 'issues'] as const).forEach((tab) => this.loadTab(tab, false, true));
    }
    const selected = this.selectedId();
    if (selected) this.catchUp(selected);
  }

  /**
   * Chats we list under a tab that the complete first page no longer has:
   * they changed (taken, closed) without us hearing it, so reload them.
   */
  private recheckMissing(tab: SupportInboxTab, items: SupportApiChat[]): void {
    const fresh = new Set(items.map((c) => c._id));
    for (const chat of this.lists()[tab]) {
      if (fresh.has(chat._id)) continue;
      this.api.chat(chat._id).subscribe({ next: (c) => this.upsert([c]), error: () => {} });
    }
  }

  private deliver(clientId: string): void {
    const item = this.outbox.get(clientId);
    if (!item) return;
    this.api
      .sendMessage(item.chatId, item.body, clientId, item.attachmentIds)
      .subscribe({
        next: ({ message }) => {
          this.outbox.delete(clientId);
          this.handleIncoming(message);
        },
        error: (error) => {
          const code = toSupportError(error).code;
          this.patchMessage(item.chatId, clientId, { status: 'failed', error: code });
          if (code === 'CONFLICT' || code === 'FORBIDDEN') {
            this.api.chat(item.chatId).subscribe({
              next: (chat) => this.upsert([chat]),
              error: () => {},
            });
          }
        },
      });
  }

  private handleIncoming(apiMessage: SupportApiMessage): void {
    const id = apiMessage.chatId;
    if (!this._threads()[id]) return;
    this.patchThread(id, (thread) => ({
      ...thread,
      messages: mergeMessages(thread.messages, [fromApiMessage(apiMessage)]),
    }));
  }

  private reloadAfter(id: string, error: unknown): Observable<never> {
    const failure = toSupportError(error);
    if (failure.code === 'CONFLICT' || failure.code === 'FORBIDDEN') {
      this.api.chat(id).subscribe({ next: (chat) => this.upsert([chat]), error: () => {} });
    }
    return throwError(() => failure);
  }

  /** Newer summaries win; out-of-order events can't roll a chat back. */
  private upsert(chats: SupportApiChat[]): void {
    if (!chats.length) return;
    this.pool.update((pool) => {
      const next = { ...pool };
      for (const chat of chats) {
        const known = next[chat._id];
        if (!known || chat.updatedAt >= known.updatedAt) next[chat._id] = chat;
      }
      return next;
    });
    for (const chat of chats) {
      if (this.isNewClientMessage(chat)) this.incomingSubject.next(chat);
    }
  }

  /** Each client message once, and only those after the first load. */
  private isNewClientMessage(chat: SupportApiChat): boolean {
    const at = chat.lastMessageAt;
    if (!at) return false;
    const previous = this.seen.get(chat._id);
    if (previous && at <= previous) return false;
    this.seen.set(chat._id, at);
    if (!this.primedAfter || chat.lastMessageSenderType !== 'client') return false;
    if (!previous && at <= this.primedAfter) return false;
    const me = this.me()?.id;
    return chat.status === 'open' && (!chat.assignedManagerId || chat.assignedManagerId === me);
  }

  /**
   * From now on, client messages notify. Chats we see later for the first
   * time only count if their message is newer than anything loaded so far
   * (a minute back from now when the inbox was empty).
   */
  private prime(): void {
    let latest = new Date(Date.now() - 60_000).toISOString();
    this.seen.forEach((at) => {
      if (at > latest) latest = at;
    });
    this.primedAfter = latest;
  }

  private filtersFor(tab: SupportInboxTab): SupportInboxFilters {
    switch (tab) {
      case 'new':
        return { status: 'open', assigned: 'unassigned', type: 'message' };
      case 'mine':
        return { status: this.mineStatus(), assigned: 'me' };
      case 'issues':
        return { status: 'open', type: 'issue' };
    }
  }

  private pageKey(tab: SupportInboxTab): string {
    return tab === 'mine' ? `mine:${this.mineStatus()}` : tab;
  }

  private setPage(key: string, changes: Partial<TabPage>): void {
    this.pages.update((pages) => ({
      ...pages,
      [key]: { ...(pages[key] || EMPTY_PAGE), ...changes },
    }));
  }

  private patchThread(
    id: string,
    change: (thread: SupportInboxThread) => SupportInboxThread,
  ): void {
    this._threads.update((threads) => ({
      ...threads,
      [id]: change(threads[id] || { messages: [], loaded: false, olderCursor: null }),
    }));
  }

  private patchMessage(
    chatId: string,
    clientId: string,
    changes: Partial<SupportMessage>,
  ): void {
    this.patchThread(chatId, (thread) => ({
      ...thread,
      messages: thread.messages.map((m) =>
        m.clientId === clientId && m.status !== 'sent' ? { ...m, ...changes } : m,
      ),
    }));
  }
}

function matchesTab(
  tab: SupportInboxTab,
  chat: SupportApiChat,
  me: string | undefined,
  mineStatus: 'open' | 'closed',
): boolean {
  switch (tab) {
    case 'new':
      return chat.status === 'open' && !chat.assignedManagerId && chat.type !== 'issue';
    case 'mine':
      return !!me && chat.assignedManagerId === me && chat.status === mineStatus;
    case 'issues':
      return chat.type === 'issue' && chat.status === 'open';
  }
}

function byActivity(a: SupportApiChat, b: SupportApiChat): number {
  return (
    (b.lastActivityAt || b.updatedAt).localeCompare(a.lastActivityAt || a.updatedAt) ||
    b._id.localeCompare(a._id)
  );
}
